import type { OrgSummary, PublicUser } from '@visora/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setAccessToken, tryRefresh } from './api';

const ACTIVE_ORG_KEY = 'visora-active-org';

interface LoginResult {
  mfaRequired?: boolean;
  mfaToken?: string;
}

interface AuthContextValue {
  user: PublicUser | null;
  orgs: OrgSummary[];
  activeOrg: OrgSummary | null;
  loading: boolean;
  login(email: string, password: string): Promise<LoginResult>;
  verifyMfa(mfaToken: string, code: string): Promise<void>;
  register(name: string, email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  reload(): Promise<void>;
  setActiveOrg(orgId: string): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  user: PublicUser & { notificationPrefs?: Record<string, boolean> | null };
  orgs: OrgSummary[];
}

interface SessionResponse {
  user: PublicUser;
  accessToken: string;
  mfaRequired?: boolean;
  mfaToken?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ORG_KEY),
  );
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const me = await api<MeResponse>('/users/me');
    setUser(me.user);
    setOrgs(me.orgs);
  }, []);

  // Bootstrap: silent refresh from the httpOnly cookie, then hydrate profile.
  useEffect(() => {
    (async () => {
      const ok = await tryRefresh();
      if (ok) await loadMe().catch(() => undefined);
      setLoading(false);
    })();
  }, [loadMe]);

  // Global 401 → drop the session.
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setOrgs([]);
    };
    window.addEventListener('visora:unauthorized', onUnauthorized);
    return () => window.removeEventListener('visora:unauthorized', onUnauthorized);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const result = await api<SessionResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
        skipRefresh: true,
      });
      if (result.mfaRequired) return { mfaRequired: true, mfaToken: result.mfaToken };
      setAccessToken(result.accessToken);
      await loadMe();
      return {};
    },
    [loadMe],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const result = await api<SessionResponse>('/auth/mfa/verify', {
        method: 'POST',
        body: { mfaToken, code },
        skipRefresh: true,
      });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const register = useCallback(
    async (name: string, email: string, password: string) => {
      const result = await api<SessionResponse>('/auth/register', {
        method: 'POST',
        body: { name, email, password },
        skipRefresh: true,
      });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST', skipRefresh: true }).catch(() => undefined);
    setAccessToken(null);
    setUser(null);
    setOrgs([]);
    localStorage.removeItem(ACTIVE_ORG_KEY);
  }, []);

  const setActiveOrg = useCallback((orgId: string) => {
    localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    setActiveOrgId(orgId);
  }, []);

  const activeOrg = useMemo(() => {
    if (orgs.length === 0) return null;
    return orgs.find((org) => org.id === activeOrgId) ?? orgs[0] ?? null;
  }, [orgs, activeOrgId]);

  const value = useMemo(
    () => ({
      user,
      orgs,
      activeOrg,
      loading,
      login,
      verifyMfa,
      register,
      logout,
      reload: loadMe,
      setActiveOrg,
    }),
    [user, orgs, activeOrg, loading, login, verifyMfa, register, logout, loadMe, setActiveOrg],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Role helper for the active org. */
export function useOrgRole(): { role: string; isAdmin: boolean; isOwner: boolean } {
  const { activeOrg } = useAuth();
  const role = activeOrg?.role ?? 'VIEWER';
  return { role, isAdmin: role === 'OWNER' || role === 'ADMIN', isOwner: role === 'OWNER' };
}
